import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '@zarya/theme';
import { ErrorBanner } from '@zarya/ui';
import { SEARCH_MIN, useSearchGlobal, type SearchResult } from '@app/api/hooks/shell';
import { t, tn } from '@app/i18n';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import s from './GlobalSearch.module.css';

type Hit = NonNullable<SearchResult['identities']>[number];
type Group = keyof SearchResult;

/**
 * Grupo de resultado (FR-014), na ordem do contrato.
 *
 * O rótulo **é o do menu**, e agora isso é literal: cada grupo aponta para a
 * mesma chave de `shell.nav.*` que o rail usa. Antes as duas listas repetiam a
 * frase, e a de 03/09 («o rótulo de `policies` é o do menu») só valia enquanto
 * alguém lembrasse de mudar os dois lugares.
 */
const GROUPS: { key: Group; navKey: string; icone: IconName }[] = [
  { key: 'identities', navKey: 'identities', icone: 'robot' },
  { key: 'resources', navKey: 'resources', icone: 'server' },
  // A chave `policies` é do contrato (`SearchResult`) e fica; o que se busca ali
  // é regra de Human Access.
  { key: 'policies', navKey: 'humanAccess', icone: 'shield' },
  // Só admin recebe usuários — o contrato devolve a lista vazia para os demais.
  { key: 'users', navKey: 'users', icone: 'users' },
];

/** Rótulo do grupo, resolvido em tempo de desenho (seletor de idioma). */
const groupTitle = (navKey: string) => t(`shell.nav.${navKey}`);

export interface GlobalSearchProps {
  aberta: boolean;
  onFechar: () => void;
}

/**
 * Busca global (T2, FR-014): `Ctrl K` ou o campo da topbar abrem; o resultado vem
 * agrupado de `searchGlobal` e escolher um item navega para o `link` que o
 * servidor devolveu.
 */
export function GlobalSearch({ aberta, onFechar }: GlobalSearchProps) {
  const navigate = useNavigate();
  const titleID = useId();
  const listID = useId();
  const fieldRef = useRef<HTMLInputElement>(null);

  const [term, setTermo] = useState('');
  const [ativo, setAtivo] = useState(0);
  // Digitar rápido não dispara uma chamada por tecla.
  const termBuscado = useDeferredValue(term);
  const search = useSearchGlobal(aberta ? termBuscado : '');
  // §3.1: com um site só, uma linha que diz sempre `default` em todo acerto é
  // ruído. Quem decide é o servidor (`SiteList.multi`), lido por este hook.
  const multiSite = useMultiSite();

  /** Resultados achatados na ordem em que aparecem — é o que o teclado percorre. */
  const itens = useMemo(() => {
    const payload = search.data;
    if (!payload) return [] as { group: string; hit: Hit }[];
    return GROUPS.flatMap(({ key, navKey }) =>
      (payload[key] ?? []).map((hit) => ({ group: groupTitle(navKey), hit })),
    );
  }, [search.data]);

  useEffect(() => {
    if (aberta) fieldRef.current?.focus();
    else {
      setTermo('');
      setAtivo(0);
    }
  }, [aberta]);

  useEffect(() => setAtivo(0), [termBuscado]);

  if (!aberta) return null;

  const short = term.trim().length < SEARCH_MIN;
  const empty = !short && !search.isFetching && itens.length === 0;

  function choose(index: number) {
    const alvo = itens[index]?.hit;
    if (!alvo?.link) return;
    onFechar();
    navigate(alvo.link);
  }

  function aoTeclar(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onFechar();
      return;
    }
    if (!itens.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAtivo((i) => (i + 1) % itens.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAtivo((i) => (i - 1 + itens.length) % itens.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(ativo);
    }
  }

  const opcaoId = (index: number) => `${listID}-opt-${index}`;
  let index = -1;

  return (
    <>
      <div className={s.scrim} onClick={onFechar} data-testid="busca-scrim" aria-hidden="true" />
      <div
        className={s.painel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleID}
        data-testid="busca-global"
      >
        <h2 className="sr-only" id={titleID}>
          {t('shell.search.title')}
        </h2>

        <div className={s.campoLinha}>
          <Icon name="search" size="sm" aria-hidden="true" />
          <input
            ref={fieldRef}
            className={s.campo}
            type="text"
            value={term}
            placeholder={t('shell.search.placeholder')}
            aria-label={t('shell.search.fieldLabel')}
            // `aria-activedescendant` no elemento que tem o foco — é o único que
            // o leitor de tela acompanha.
            role="combobox"
            aria-expanded={itens.length > 0}
            aria-controls={listID}
            aria-autocomplete="list"
            aria-activedescendant={itens.length ? opcaoId(ativo) : undefined}
            autoComplete="off"
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={aoTeclar}
          />
          <kbd className={s.tecla}>{t('shell.search.escKey')}</kbd>
        </div>

        <div className={s.resultado}>
          {search.error ? (
            <ErrorBanner error={search.error} title={t('shell.search.error')} />
          ) : short ? (
            // `tn` porque o mínimo é um número: com `SEARCH_MIN = 1` a frase antiga
            // dizia «ao menos 1 caracteres».
            <p className={s.dica}>{tn('shell.search.tooShort', SEARCH_MIN)}</p>
          ) : empty ? (
            <p className={s.dica}>{t('shell.search.empty', { term: term.trim() })}</p>
          ) : (
            <ul
              className={s.lista}
              id={listID}
              role="listbox"
              aria-label={t('shell.search.resultsLabel')}
            >
              {GROUPS.map(({ key, navKey, icone }) => {
                const hits = search.data?.[key] ?? [];
                if (!hits.length) return null;
                return (
                  <li key={key}>
                    <h3 className={s.grupo}>{groupTitle(navKey)}</h3>
                    <ul className={s.grupoLista}>
                      {hits.map((hit) => {
                        index += 1;
                        const meu = index;
                        return (
                          <li
                            key={hit.id}
                            id={opcaoId(meu)}
                            role="option"
                            aria-selected={meu === ativo}
                            className={[s.item, meu === ativo ? s.itemAtivo : '']
                              .filter(Boolean)
                              .join(' ')}
                            tabIndex={-1}
                            onMouseEnter={() => setAtivo(meu)}
                            onClick={() => choose(meu)}
                            // O teclado normal vive no campo (padrão APG), mas a
                            // linha é focável por programa — leitor de tela em modo
                            // formulário chega aqui, e Enter/Espaço têm de escolher.
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                choose(meu);
                              }
                            }}
                          >
                            <Icon name={icone} size="sm" aria-hidden="true" />
                            <span className={s.itemTexto}>
                              <span className={s.itemTitulo}>{hit.title}</span>
                              {hit.subtitle ? (
                                // Identificador técnico em monoespaçada (FR-133).
                                <span className={s.itemSub}>{hit.subtitle}</span>
                              ) : null}
                              {/* Faixa B (§5.6, forma 2): o site do acerto.
                                  Linha PRÓPRIA, e não composta no `subtitle`: aquele
                                  campo é texto pronto do servidor para outra coisa
                                  (imagem, `gateway_id`, e-mail), e juntá-los obrigaria
                                  a tela a desmontá-lo para mostrar um deles.

                                  `site_slug` vem ausente em política e usuário — não
                                  são workload nenhum —, então a condição cobre os dois
                                  casos sem os distinguir. Só com `multi` (§3.1). */}
                              {multiSite && hit.site_slug ? (
                                <span className={s.itemSite}>
                                  {t('shell.search.site', { site: hit.site_slug })}
                                </span>
                              ) : null}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
